import type { StoreCustomer } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

/**
 * A counter's customers with a valid scan, as last fetched. Cached across pages
 * for the same reason as the review queue; `markStale` after an approval, since
 * a customer whose last scan was answered may drop off the list. The list is one
 * counter's (`entry`, a shop × location id): another counter's reads and adds
 * never land on it.
 */
export class CustomersRepository extends ChangeNotifier {
  #status: LoadStatus = { kind: "idle" };
  #customers: readonly StoreCustomer[] = [];
  #stale = false;
  #entry: string | null = null;
  /** Numbers the reads, so an answer to an older one never lands over a newer one. */
  #asked = 0;
  /** Customers added while a read was on its way: its answer was asked before them. */
  #added: StoreCustomer[] = [];
  /** Every customer listed or added at this counter since it was read first, by their last record. */
  #known = new Map<string, StoreCustomer>();

  get status(): LoadStatus {
    return this.#status;
  }

  get customers(): readonly StoreCustomer[] {
    return this.#customers;
  }

  get isStale(): boolean {
    return this.#stale;
  }

  /** The counter the list is for, or the one being read. */
  get entry(): string | null {
    return this.#entry;
  }

  find(id: string): StoreCustomer | undefined {
    return this.#customers.find((c) => c.id === id);
  }

  /**
   * A customer this counter has listed, though a later read left them out (their last scan deleted or answered): a
   * page that shows them keeps their name, as iOS keeps the customer it shows. Another counter's are never named.
   */
  named(id: string): StoreCustomer | undefined {
    return this.find(id) ?? this.#known.get(id);
  }

  /** A read of `entry`'s customers starts; its answer lands only if no newer read started. Another counter's list goes. */
  setLoading(entry: string): number {
    if (entry !== this.#entry) {
      this.#customers = [];
      this.#known.clear();
    }
    this.#entry = entry;
    this.#stale = false;
    this.#added = [];
    this.#set({ kind: "loading" });
    return ++this.#asked;
  }

  setCustomers(customers: readonly StoreCustomer[], asked: number): void {
    if (asked !== this.#asked) return;
    const added = this.#added.filter((a) => !customers.some((c) => c.id === a.id));
    this.#customers = [...added, ...customers];
    for (const c of this.#customers) this.#known.set(c.id, c);
    this.#added = [];
    this.#set({ kind: "loaded" });
  }

  setError(message: string, asked: number): void {
    if (asked !== this.#asked) return;
    this.#set({ kind: "error", message });
  }

  /** A customer the shop just added at `entry`: listed at once, though they have no scans yet. */
  add(customer: StoreCustomer, entry: string): void {
    if (entry !== this.#entry) return;
    this.#customers = [customer, ...this.#customers.filter((c) => c.id !== customer.id)];
    this.#known.set(customer.id, customer);
    if (this.#status.kind === "loading") this.#added = [customer, ...this.#added.filter((c) => c.id !== customer.id)];
    this.notifyListeners();
  }

  markStale(): void {
    if (this.#stale) return;
    this.#stale = true;
    this.notifyListeners();
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
